// Components/ModuleDetails.js
import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { 
  Typography, 
  Card, 
  Space, 
  Button, 
  Spin, 
  Alert, 
  Tag, 
  Row, 
  Col,
  Divider,
  List,
  Badge
} from "antd";
import {
  ClockCircleOutlined,
  EditOutlined,
  CalendarOutlined,
  ArrowLeftOutlined,
  CheckCircleOutlined,
  SettingOutlined,
  BookOutlined,
  CheckSquareOutlined,
  PlayCircleOutlined,
  PlusOutlined,
  UserOutlined,
  FileTextOutlined,
  EyeOutlined
} from "@ant-design/icons";
import useModuleDetails from "../Hooks/Courses/useModuleDetails";
import useCourseDetails from "../Hooks/Courses/useCourseDetails";

const { Title, Text, Paragraph } = Typography;

const ModuleDetails = () => {
  const { courseId, moduleId } = useParams();
  const navigate = useNavigate();
  
  // Use the hook to fetch module data
  const { module, loading, error } = useModuleDetails(moduleId);
  const { course } = useCourseDetails(courseId);
    const isCoursePublished = course && course.isPublished;

  

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatTime = (timeString) => {
    if (!timeString || timeString === "00:00:00") return "Not specified";
    
    const [hours, minutes, seconds] = timeString.split(":");
    const hoursInt = parseInt(hours);
    const minutesInt = parseInt(minutes);
    
    if (hoursInt === 0 && minutesInt === 0) return "Not specified";
    if (hoursInt === 0) return `${minutesInt}m`;
    if (minutesInt === 0) return `${hoursInt}h`;
    
    return `${hoursInt}h ${minutesInt}m`;
  };

  // Calculate total time from pages
  const calculateTotalTime = (pages) => {
    if (!pages || pages.length === 0) return "00:00:00";
    
    let totalSeconds = 0;
    pages.forEach(page => {
      if (page.estimatedTime && page.estimatedTime !== "00:00:00") {
        const [hours, minutes, seconds] = page.estimatedTime.split(':').map(Number);
        totalSeconds += hours * 3600 + minutes * 60 + seconds;
      }
    });
    
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;
    
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: "50px" }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="Error Loading Module Details"
        description={error}
        type="error"
        showIcon
        style={{ margin: "20px" }}
        action={
          <Button size="small" onClick={() => navigate(-1)}>
            Go Back
          </Button>
        }
      />
    );
  }

  if (!module) {
    return (
      <Alert
        message="Module Not Found"
        description="The requested module could not be found."
        type="warning"
        showIcon
        style={{ margin: "20px" }}
        action={
          <Button size="small" onClick={() => navigate(-1)}>
            Go Back
          </Button>
        }
      />
    );
  }

  const totalTime = calculateTotalTime(module.pages);

return (
  <Card
    className="card-professional"
    style={{ padding: "24px" }}
    title={
      <Space direction="vertical" size={2}>
        <Title level={2} style={{ margin: 0, color: "white" }}>
          {module.title}
        </Title>

        <Space size="middle">
          <Tag
            color={module.isFinalized ? "green" : "orange"}
            icon={module.isFinalized ? <CheckCircleOutlined /> : <SettingOutlined />}
          >
            {module.isFinalized ? "Finalized" : "Draft"}
          </Tag>

          <Text style={{ color: "rgba(255,255,255,0.8)" }}>
            Module ID: {module.id}
          </Text>

          <Text style={{ color: "rgba(255,255,255,0.8)" }}>
            Course ID: {module.courseId}
          </Text>
            <Tag
              color={isCoursePublished ? "green" : "orange"}
              icon={isCoursePublished ? <EyeOutlined /> : <EditOutlined />}
            >
              {isCoursePublished ? "Published" : "Draft"}
            </Tag>
        </Space>
      </Space>
    }
    extra={
      <Button
        type="default"
        icon={<ArrowLeftOutlined />}
        onClick={() =>
          navigate(`/proAdmin-dashboard/courseDetails/${courseId}`)
          
        }
        
      >
        Back to Course
      </Button>
    }
  
  >
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      {/* Show message if module has no assessment */}
      {(!module.assessment && !module.hasAssessment) && (
        <Alert
          message="No Assessment Found for This Module"
          description={
            <Space direction="vertical" size="small">
              <Text>
                This module currently does not have any assessment linked.
                Adding an assessment helps evaluate learners&apos; understanding and track progress.
              </Text>
              <Text type="secondary" italic>
                You can add a quiz, test, or assignment for this module using the button below.
              </Text>
            </Space>
          }
          type="info"
          showIcon
                      action={
              // Only show Add Assessment button if course is not published
              !isCoursePublished && (
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() =>
                    navigate(`/proAdmin-dashboard/courses/${courseId}/modules/${module.id}/edit-assessment`)
                  }
                >
                  Add Assessment
                </Button>
              )
            }

          style={{ marginBottom: 24 }}
        />
      )}

      <Row gutter={[24, 16]}>
        <Col xs={24} lg={16}>
          {/* Module Description */}
          <div style={{ background: 'white', padding: '24px', borderRadius: '8px', border: '1px solid #f0f0f0' }}>
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
              <Space>
                <FileTextOutlined style={{ color: '#1890ff' }} />
                <Text strong style={{ fontSize: '16px' }}>Module Description</Text>
              </Space>
              <Paragraph style={{ fontSize: "16px", lineHeight: "1.6", margin: 0 }}>
                {module.description}
              </Paragraph>
            </Space>
          </div>

          {/* Module Pages */}
          <div style={{ background: 'white', padding: '24px', borderRadius: '8px', border: '1px solid #f0f0f0', marginTop: '16px' }}>
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
              <Space>
                <BookOutlined style={{ color: '#1890ff' }} />
                <Text strong style={{ fontSize: '16px' }}>Module Pages</Text>
                <Badge
                  count={module.pages?.length || 0}
                  showZero
                  color={module.pages?.length > 0 ? "blue" : "default"}
                />
              </Space>
              {module.pages && module.pages.length > 0 ? (
                <List
                  size="small"
                  dataSource={module.pages}
                  renderItem={(page, index) => (
                    <List.Item
                      key={page.id}
                      actions={[
                        <Button 
                          key="preview" 
                          type="link" 
                          icon={<EyeOutlined />}
                          onClick={() => navigate(`/proAdmin-dashboard/courses/${courseId}/modules/${moduleId}/pages/${page.id}`)}
                        >
                          Preview
                        </Button>
                        
                      ]}
                    >
                      <List.Item.Meta
                        title={`Page ${page.pageNumber}`}
                        description={`Time: ${formatTime(page.estimatedTime)}`}
                      />
                    </List.Item>
                  )}
                />
              ) : (
                <div style={{ textAlign: "center", padding: "20px" }}>
                  <BookOutlined style={{ fontSize: "32px", color: "#d9d9d9", marginBottom: "8px" }} />
                  <Title level={5} type="secondary">
                    No Pages
                  </Title>
                </div>
              )}
            </Space>
          </div>

          {/* Assessment */}
          {module.assessment && (
            <div style={{ background: 'white', padding: '24px', borderRadius: '8px', border: '1px solid #f0f0f0', marginTop: '16px' }}>
              <Space direction="vertical" style={{ width: '100%' }} size="middle">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Space>
                    <CheckSquareOutlined style={{ color: '#1890ff' }} />
                    <Text strong style={{ fontSize: '16px' }}>Assessment</Text>
                  </Space>
                  <Space>
                    <Button
                      type="link"
                      icon={<EyeOutlined />}
                      onClick={() =>
                        navigate(
                          `/proAdmin-dashboard/courses/${courseId}/modules/${moduleId}/edit-assessment?assessmentId=${module.assessment.id}&mode=preview`
                        )
                      }
                    >
                      Preview
                    </Button>

                                         {!isCoursePublished && (
                        <Button
                          type="link"
                          icon={<EditOutlined />}
                          onClick={() =>
                            navigate(
                              `/proAdmin-dashboard/courses/${courseId}/modules/${moduleId}/edit-assessment?assessmentId=${module.assessment.id}`
                            )
                          }
                        >
                          Edit
                        </Button>
                      )}
                  </Space>
                </div>
                <List>
                  <List.Item>
                    <List.Item.Meta
                      avatar={
                        <CheckSquareOutlined style={{ fontSize: "24px", color: "#52c41a" }} />
                      }
                      title={<Text strong>{module.assessment.title}</Text>}
                      description={
                        <Space direction="vertical" size="small">
                          <Text>Estimated Time: {formatTime(module.assessment.estimatedTime)}</Text>
                          <Text>Assessment ID: {module.assessment.id}</Text>
                          <Text>Created: {formatDate(module.assessment.createdOn)}</Text>
                        </Space>
                      }
                    />
                  </List.Item>
                </List>
              </Space>
            </div>
          )}
        </Col>

        {/* Right Column */}
        <Col xs={24} lg={8}>
          <div style={{ background: 'white', padding: '24px', borderRadius: '8px', border: '1px solid #f0f0f0' }}>
            <Space direction="vertical" style={{ width: "100%" }} size="middle">
              <Space>
                <UserOutlined style={{ color: '#1890ff' }} />
                <Text strong style={{ fontSize: '16px' }}>Module Overview</Text>
              </Space>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <Text>Module Time:</Text>
                <Space>
                  <ClockCircleOutlined />
                  <Text>{formatTime(module.estimatedTime)}</Text>
                </Space>
              </div>

              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <Text>Total Pages:</Text>
                <Text>{module.pages?.length || 0}</Text>
              </div>

              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <Text>Estimated Time:</Text>
                <Text>{formatTime(totalTime)}</Text>
              </div>

              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <Text>Module Order:</Text>
                <Text>{module.order}</Text>
              </div>

              <Divider />

              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <Text>Created:</Text>
                <Space>
                  <CalendarOutlined />
                  <Text>{formatDate(module.createdOn)}</Text>
                </Space>
              </div>

              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <Text>Last Updated:</Text>
                <Space>
                  <CalendarOutlined />
                  <Text>{formatDate(module.lastModifiedOn)}</Text>
                </Space>
              </div>
            </Space>
          </div>


{/* Quick Actions */}
{!isCoursePublished && (
  <div style={{ background: 'white', padding: '24px', borderRadius: '8px', border: '1px solid #f0f0f0', marginTop: '16px' }}>
    <Space direction="vertical" style={{ width: "100%" }} size="middle">
      <Text strong style={{ fontSize: '16px' }}>Quick Actions</Text>
      <Button
        type="primary"
        block
        icon={<EditOutlined />}
        size="large"
        onClick={() =>
          navigate(`/proAdmin-dashboard/courses/${courseId}/add-module?moduleId=${module.id}`)
        }
      >
        Edit Module
      </Button>

      {!module.assessment && (
        <Button
          type="dashed"
          block
          icon={<PlusOutlined />}
          size="large"
          onClick={() =>
            navigate(`/proAdmin-dashboard/courses/${courseId}/modules/${module.id}/edit-assessment`)
          }
        >
          Add Assessment
        </Button>
      )}
    </Space>
  </div>
)}
        </Col>
      </Row>
    </Space>
  </Card>
);

};

export default ModuleDetails;